import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getENVData } from '@/configs/enviornment'

export const axiosInstance = axios.create({
  baseURL: getENVData().baseURL,
})

/**
 * PERSISTENT AUTH STORE
 * ES modules are singletons — this object is created once and shared
 * across all imports. No need to attach anything to `window`.
 */
const authStore: { getter: (() => Promise<string>) | null } = {
  getter: null,
}
axiosInstance.interceptors.request.use(
  async config => {
    if (authStore.getter) {
      try {
        const token = await authStore.getter()

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (error) {
        console.error('Failed to get access token:', error)
      }
    }

    return config
  },
  error => Promise.reject(error),
)

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      authStore.getter
    ) {
      originalRequest._retry = true

      try {
        const newToken = await authStore.getter()

        if (newToken) {
          if (
            originalRequest.headers &&
            typeof originalRequest.headers.set === 'function'
          ) {
            originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
          } else {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          }

          Object.assign(axiosInstance.defaults.headers.common, {
            Authorization: `Bearer ${newToken}`,
          })

          return axiosInstance(originalRequest)
        }
      } catch (err) {
        console.error('Retry after 401 failed:', err)
      }
    }

    if (
      error.response?.status === 401 &&
      !window.location.pathname.includes('/app/expired')
    ) {
      window.location.href = `${window.location.origin}/app/expired`
    }

    // if (
    //   error.response?.status === 403 &&
    //   !window.location.pathname.includes('/app/unauthorized')
    // ) {
    //   window.location.href = `${window.location.origin}/app/unauthorized`
    // }

    return Promise.reject(error)
  },
)

/** Call once after Auth0 is ready. Safe to call multiple times. */
export const setAuthInterceptor = (
  getAccessTokenSilently: () => Promise<string>,
) => {
  authStore.getter = getAccessTokenSilently
}

export default axiosInstance
